import { useState, useCallback, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOSchema from '@/components/feature/SEOSchema';

export default function AboutPage() {
  const [activeTab, setActiveTab] = useState<'intro' | 'testimonial' | 'contact'>('intro');
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleContactSubmit = useCallback(async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypotVal = (formData.get('phone_alt') as string || '').trim();
    if (honeypotVal) {
      setFormStatus('success');
      form.reset();
      return;
    }
    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9on0r0senbniolesi00', {
        method: 'POST',
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  }, []);

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: '关于我们' },
  ];

  const faqSchema = {
    mainEntity: [
      {
        '@type': 'Question',
        name: '虫虫动漫的漫画真的是免费的吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的！虫虫动漫平台上所有漫画内容完全免费阅读，无需付费，无需订阅，打开即看。我们致力于为漫画爱好者提供无障碍的阅读体验。',
        },
      },
      {
        '@type': 'Question',
        name: '虫虫动漫有哪些漫画类型？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '虫虫动漫拥有十二大分类，包括少年热血、恋爱校园、奇幻冒险、搞笑日常、悬疑推理、动作格斗、科幻未来、恐怖惊悚、治愈日常、历史古风、运动竞技等，总有一款适合你。',
        },
      },
      {
        '@type': 'Question',
        name: '如何使用下拉式滚动浏览功能？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '进入任意漫画章节后，您只需向下滚动页面即可连续阅读，无需点击翻页按钮。下拉式滚动浏览让阅读更流畅、更沉浸。',
        },
      },
      {
        '@type': 'Question',
        name: '漫画更新频率是怎样的？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '热门漫画通常保持每周更新，部分作品可能会有更频繁的更新。您可以在漫画详情页查看具体的更新日期和最新章节信息。',
        },
      },
      {
        '@type': 'Question',
        name: '如何找到我想看的漫画？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过顶部的搜索框输入漫画名称进行搜索，也可以通过分类页面按类型筛选，或者浏览首页的热门排行和编辑推荐来发现好作品。',
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOSchema type="WebSite" />
      <SEOSchema type="Organization" />
      <SEOSchema type="WebPage" data={{
        name: '关于我们 - 虫虫动漫',
        description: '了解虫虫动漫的品牌故事、用户口碑和联系方式。虫虫动漫致力于打造最好的免费漫画在线阅读平台。',
      }} />
      <SEOSchema type="FAQPage" data={faqSchema} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-6">
            <Breadcrumb items={breadcrumbItems} />

            <div className="mt-4 mb-8">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-900">关于我们</h1>
              <p className="text-sm text-foreground-500 mt-1">了解虫虫动漫的品牌故事与发展历程</p>
            </div>

            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 mb-8 w-fit">
              <button
                onClick={() => setActiveTab('intro')}
                className={`px-5 py-2 text-sm rounded-full transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === 'intro'
                    ? 'bg-background-50 text-foreground-900 font-medium shadow-sm'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                品牌介绍
              </button>
              <button
                onClick={() => setActiveTab('testimonial')}
                className={`px-5 py-2 text-sm rounded-full transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === 'testimonial'
                    ? 'bg-background-50 text-foreground-900 font-medium shadow-sm'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                用户口碑
              </button>
              <button
                onClick={() => setActiveTab('contact')}
                className={`px-5 py-2 text-sm rounded-full transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === 'contact'
                    ? 'bg-background-50 text-foreground-900 font-medium shadow-sm'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                联系我们
              </button>
            </div>

            {activeTab === 'intro' && (
              <section aria-label="品牌介绍">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
                  <div className="relative h-[300px] md:h-[400px] rounded-lg overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=A%20cozy%20reading%20corner%20with%20manga%20books%20on%20wooden%20shelves%2C%20warm%20ambient%20lighting%2C%20comfortable%20armchair%2C%20steaming%20cup%20of%20tea%2C%20soft%20natural%20light%20from%20window%2C%20peaceful%20atmosphere%2C%20Japanese%20aesthetic%2C%20cozy%20interior%20design%2C%20warm%20neutral%20tones&width=1200&height=800&seq=about-hero-01&orientation=landscape"
                      alt="虫虫动漫品牌形象"
                      className="w-full h-full object-cover object-top"
                      title="虫虫动漫 - 免费漫画在线阅读平台"
                    />
                  </div>
                  <div className="flex flex-col justify-center">
                    <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-4">
                      关于虫虫动漫
                    </h2>
                    <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                      <p>
                        虫虫动漫成立于2020年，是一个专注于免费漫画在线阅读的平台。我们的使命是让每一位漫画爱好者都能随时随地、轻松畅享高品质的漫画内容。
                      </p>
                      <p>
                        平台目前已收录超过<strong className="text-foreground-800">1200部</strong>精选漫画作品，涵盖少年热血、恋爱校园、奇幻冒险、搞笑日常等<strong className="text-foreground-800">十二大分类</strong>，满足不同读者的阅读偏好。
                      </p>
                      <p>
                        我们采用创新的下拉式滚动浏览技术，让阅读体验更加流畅自然。读者只需向下滚动页面，即可沉浸式地连续阅读，告别繁琐的翻页操作。
                      </p>
                      <p>
                        虫虫动漫始终秉持"免费、优质、便捷"的理念。平台上所有漫画内容完全免费，无需付费订阅，打开即看。我们致力于消除阅读门槛，让优质的漫画内容触手可及。
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
                  <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                    <div className="text-3xl font-heading text-primary-500 mb-1">2020</div>
                    <p className="text-sm text-foreground-500">创立年份</p>
                  </div>
                  <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                    <div className="text-3xl font-heading text-primary-500 mb-1">1200+</div>
                    <p className="text-sm text-foreground-500">收录漫画</p>
                  </div>
                  <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                    <div className="text-3xl font-heading text-primary-500 mb-1">500万+</div>
                    <p className="text-sm text-foreground-500">月活用户</p>
                  </div>
                  <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                    <div className="text-3xl font-heading text-primary-500 mb-1">100%</div>
                    <p className="text-sm text-foreground-500">免费阅读</p>
                  </div>
                </div>

                <div className="bg-background-100 rounded-lg p-6 md:p-8">
                  <h3 className="text-lg font-heading text-foreground-900 mb-4">我们的优势</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex gap-3">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                        <i className="ri-shield-check-line"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-900 mb-1">正版内容授权</h4>
                        <p className="text-xs text-foreground-500 leading-relaxed">所有漫画内容均经过正版授权，保证内容的合法合规性，让读者放心阅读。</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100 text-accent-500">
                        <i className="ri-smartphone-line"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-900 mb-1">多端适配阅读</h4>
                        <p className="text-xs text-foreground-500 leading-relaxed">完美适配PC端和移动端，无论使用什么设备都能获得出色的阅读体验。</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                        <i className="ri-time-line"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-900 mb-1">实时同步更新</h4>
                        <p className="text-xs text-foreground-500 leading-relaxed">热门漫画每日同步更新，追更不等待，第一时间畅读最新章节。</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100 text-accent-500">
                        <i className="ri-user-heart-line"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-900 mb-1">用户至上体验</h4>
                        <p className="text-xs text-foreground-500 leading-relaxed">简洁清爽的界面设计，无广告干扰，让阅读回归纯粹的快乐。</p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {activeTab === 'testimonial' && (
              <section aria-label="用户口碑">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
                  <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }, (_, i) => (
                        <i key={i} className="ri-star-fill text-accent-400 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      "虫虫动漫真的是漫画爱好者的天堂！免费漫画资源太丰富了，而且更新速度很快。下拉式滚动浏览的体验非常好，看漫画再也不用不停点下一页了。已经推荐给身边所有喜欢看漫画的朋友！"
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 text-sm font-bold">
                        李
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">李先生</p>
                        <p className="text-xs text-foreground-400">资深漫画爱好者 · 使用2年</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }, (_, i) => (
                        <i key={i} className="ri-star-fill text-accent-400 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      "作为一个每天都要看漫画的人，虫虫动漫的分类功能太方便了。想看什么类型直接筛选就行，推荐算法也很准，总能发现我喜欢的作品。最重要的是完全免费，良心平台！"
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-sm font-bold">
                        王
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">王同学</p>
                        <p className="text-xs text-foreground-400">大学生 · 使用1年</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }, (_, i) => (
                        <i key={i} className="ri-star-fill text-accent-400 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      "从小学就开始看漫画，用过很多平台，虫虫动漫的界面是最清爽的。没有烦人的弹窗和广告，阅读体验非常棒。种类也很全，热门的冷门的都能找到，简直完美。"
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 text-sm font-bold">
                        张
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">张先生</p>
                        <p className="text-xs text-foreground-400">上班族 · 使用3年</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }, (_, i) => (
                        <i key={i} className="ri-star-fill text-accent-400 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      "虫虫动漫的下拉式滚动浏览太适合我了！通勤路上单手就能滑动看漫画，不用费劲去点翻页按钮。而且加载速度很快，即使用手机流量也不会卡顿，体验一级棒。"
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-sm font-bold">
                        陈
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">陈女士</p>
                        <p className="text-xs text-foreground-400">通勤族 · 使用1年</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-accent-50 rounded-lg p-6 md:p-8 text-center">
                  <h3 className="text-lg font-heading text-foreground-900 mb-2">用户评分</h3>
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <span className="text-4xl font-heading text-accent-500">9.5</span>
                    <span className="text-sm text-foreground-500">/ 10</span>
                  </div>
                  <div className="flex items-center justify-center gap-0.5 mb-2">
                    {Array.from({ length: 5 }, (_, i) => (
                      <i key={i} className="ri-star-fill text-accent-400 text-lg"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-500">基于 50,000+ 用户真实评价</p>
                </div>
              </section>
            )}

            {activeTab === 'contact' && (
              <section aria-label="联系我们">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2">
                    <h2 className="text-lg font-heading text-foreground-900 mb-4">给我们留言</h2>
                    <form
                      className="bg-background-50 border border-background-200/70 rounded-lg p-6 space-y-4"
                      data-readdy-form=""
                      onSubmit={handleContactSubmit}
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="contact-name" className="block text-sm text-foreground-700 mb-1">姓名</label>
                          <input
                            type="text"
                            id="contact-name"
                            name="name"
                            className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 text-foreground-800"
                            placeholder="请输入您的姓名"
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-email" className="block text-sm text-foreground-700 mb-1">邮箱</label>
                          <input
                            type="email"
                            id="contact-email"
                            name="email"
                            className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 text-foreground-800"
                            placeholder="请输入您的邮箱"
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="contact-subject" className="block text-sm text-foreground-700 mb-1">主题</label>
                        <input
                          type="text"
                          id="contact-subject"
                          name="subject"
                          className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 text-foreground-800"
                          placeholder="请输入留言主题"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-sm text-foreground-700 mb-1">留言内容</label>
                        <textarea
                          id="contact-message"
                          name="message"
                          rows={5}
                          maxLength={500}
                          className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 text-foreground-800 resize-none"
                          placeholder="请输入您的留言内容（最多500字）"
                        ></textarea>
                      </div>
                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="honeypot-field"
                      />
                      <button
                        type="submit"
                        disabled={formStatus === 'submitting'}
                        className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {formStatus === 'submitting' ? '发送中...' : '发送留言'}
                      </button>
                      {formStatus === 'success' && (
                        <p className="text-sm text-green-600 mt-2">留言发送成功！我们会尽快回复您。</p>
                      )}
                      {formStatus === 'error' && (
                        <p className="text-sm text-red-500 mt-2">{formError}</p>
                      )}
                    </form>
                  </div>

                  <div className="space-y-6">
                    <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                      <h3 className="text-sm font-semibold text-foreground-900 mb-4">联系方式</h3>
                      <div className="space-y-4">
                        <div className="flex gap-3">
                          <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                            <i className="ri-mail-line"></i>
                          </div>
                          <div>
                            <p className="text-xs text-foreground-400">电子邮箱</p>
                            <p className="text-sm text-foreground-700">contact@chongchongmanhua.com</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100 text-accent-500">
                            <i className="ri-wechat-line"></i>
                          </div>
                          <div>
                            <p className="text-xs text-foreground-400">微信公众号</p>
                            <p className="text-sm text-foreground-700">虫虫动漫Official</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                            <i className="ri-weibo-line"></i>
                          </div>
                          <div>
                            <p className="text-xs text-foreground-400">官方微博</p>
                            <p className="text-sm text-foreground-700">@虫虫动漫</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                      <h3 className="text-sm font-semibold text-foreground-900 mb-4">常见问题</h3>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm font-medium text-foreground-800">漫画真的免费吗？</p>
                          <p className="text-xs text-foreground-500 mt-1">是的，所有内容完全免费。</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-800">需要注册账号吗？</p>
                          <p className="text-xs text-foreground-500 mt-1">无需注册，打开即看。</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-800">支持手机阅读吗？</p>
                          <p className="text-xs text-foreground-500 mt-1">完美支持移动端阅读。</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}